import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, SectionCard, LoadingScreen, StatusBadge } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

function VerifyComplaintInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const { id } = useParams();
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [action, setAction] = useState('verify');
  const [deadlineDays, setDeadlineDays] = useState(7);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [lightbox, setLightbox] = useState(null); // { url, isVideo }

  useEffect(() => {
    api.get(`/complaints/${id}`)
      .then(r => setComplaint(r.data))
      .catch(() => setError('Failed to load complaint'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleVerify = async () => {
    setProcessing(true); setError('');
    try {
      await api.post(`/admin/verify/${id}`, { action: 'verify', deadline_days: deadlineDays });
      setSuccess('Complaint verified and escalated to authority.');
      setTimeout(() => navigate('/admin/pending-complaints'), 1800);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to verify complaint');
    } finally { setProcessing(false); }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) { setError('Please provide a reason for rejection.'); return; }
    setProcessing(true); setError('');
    try {
      await api.post(`/admin/verify/${id}`, { action: 'reject', rejection_reason: rejectionReason.trim() });
      setSuccess('Complaint rejected.');
      setTimeout(() => navigate('/admin/pending-complaints'), 1800);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to reject complaint');
    } finally { setProcessing(false); }
  };

  if (loading) return <LoadingScreen />;

  if (!complaint) {
    return (
      <div style={s.page}>
        <div style={s.layout}>
          {sidebarOpen && <Sidebar collapsed={false} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
          <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0) }}>
            <div style={s.content}>
              <div style={s.alertErr}>Complaint not found.</div>
              <button style={s.btnSecondary} onClick={() => navigate('/admin/pending-complaints')}>Back</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabStyle = (active) => ({
    flex: 1, padding: '10px 0',
    background: active ? theme.accentGrad : 'rgba(255,255,255,0.08)',
    color: active ? '#fff' : 'rgba(255,255,255,0.7)',
    border: `1px solid ${active ? 'transparent' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
  });

  const deadlineOpts = [
    { days: 3, label: '3 Days', sub: 'Urgent' },
    { days: 7, label: '7 Days', sub: 'Normal' },
    { days: 14, label: '14 Days', sub: 'Complex' },
  ];

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar collapsed={false} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Review Complaint"
            subtitle="Verify or reject this complaint"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/admin/pending-complaints')}>
                Back to Pending
              </button>
            }
          />
          <div style={s.content}>
            {error   && <div style={s.alertErr}>{error}</div>}
            {success && <div style={s.alertOk}>{success}</div>}

            <PageHeader
              icon="🔍"
              title="Review Complaint"
              subtitle={complaint ? `Submitted by ${complaint.student_name || 'Unknown'}` : 'Loading...'}
              stats={complaint ? [
                { label: 'Department', value: complaint.department || '—' },
                { label: 'Status', value: complaint.status },
              ] : []}
            />

            <SectionCard
              title={complaint.title}
              subtitle={`Submitted by ${complaint.student_name || 'Unknown'} · ${complaint.department || ''}`}
              delay="0.05s"
              action={<StatusBadge status={complaint.status} />}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 18 }}>
                {[['Department', complaint.department], ['Submitted', complaint.created_at ? new Date(complaint.created_at).toLocaleString() : '—'], ['Type', complaint.complaint_type || '—']].map(([label, val]) => (
                  <div key={label} style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: 9, border: '1px solid rgba(255,255,255,0.12)' }}>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, padding: '14px', background: 'rgba(255,255,255,0.06)', borderRadius: 9, border: '1px solid rgba(255,255,255,0.12)' }}>
                {complaint.description}
              </div>
              {complaint.images?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Attachments ({complaint.images.length})
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {complaint.images.map((img, i) => {
                      const url = `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/uploads/complaints/${img}`;
                      const isVideo = /\.(mp4|mov|avi|mkv|webm)$/i.test(img);
                      return isVideo ? (
                        <div key={i} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setLightbox({ url, isVideo: true })}>
                          <video src={url} style={{ width: 160, height: 100, objectFit: 'cover', borderRadius: 8, border: `1px solid ${theme.border}`, display: 'block' }} />
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', borderRadius: 8 }}>
                            <span style={{ fontSize: 28 }}>▶</span>
                          </div>
                        </div>
                      ) : (
                        <img key={i} src={url} alt={`Attachment ${i + 1}`}
                          style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: `1px solid ${theme.border}`, transition: 'transform 0.15s' }}
                          onClick={() => setLightbox({ url, isVideo: false })}
                          onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Lightbox */}
              {lightbox && (
                <div
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}
                  onClick={() => setLightbox(null)}
                >
                  <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 20, right: 28, background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer', lineHeight: 1 }}>✕</button>
                  {lightbox.isVideo ? (
                    <video src={lightbox.url} controls autoPlay
                      style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 10 }}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <img src={lightbox.url} alt="Full size"
                      style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 10, objectFit: 'contain', display: 'block' }}
                      onClick={e => e.stopPropagation()}
                      onError={e => { e.currentTarget.style.display='none'; e.currentTarget.nextSibling.style.display='block'; }}
                    />
                  )}
                  <a href={lightbox.url} target="_blank" rel="noreferrer"
                    style={{ display: 'none', color: '#a5b4fc', fontSize: 13, textDecoration: 'underline' }}
                    onClick={e => e.stopPropagation()}>
                    Open image directly
                  </a>
                </div>
              )}
            </SectionCard>

            <div style={{ marginTop: 16 }}>
              <SectionCard title="Take Action" delay="0.15s">
                <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                  <button style={tabStyle(action === 'verify')} onClick={() => setAction('verify')}>Verify Complaint</button>
                  <button style={tabStyle(action === 'reject')} onClick={() => setAction('reject')}>Reject Complaint</button>
                </div>

                {action === 'verify' ? (
                  <div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginBottom: 16 }}>Set the resolution deadline for the authority.</div>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 22 }}>
                      {deadlineOpts.map(opt => (
                        <div key={opt.days} className="ur-card-hover"
                          style={{ flex: 1, padding: '12px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                            border: `1px solid ${deadlineDays === opt.days ? COLORS.purple : 'rgba(255,255,255,0.15)'}`,
                            background: deadlineDays === opt.days ? COLORS.purple + '18' : 'rgba(255,255,255,0.06)',
                            transition: 'all 0.2s' }}
                          onClick={() => setDeadlineDays(opt.days)}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: deadlineDays === opt.days ? '#fff' : 'rgba(255,255,255,0.8)' }}>{opt.label}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 3 }}>{opt.sub}</div>
                        </div>
                      ))}
                    </div>
                    <button className="ur-btn-hover" style={{ ...s.btnSuccess, width: '100%', justifyContent: 'center', display: 'flex', padding: '12px' }}
                      onClick={handleVerify} disabled={processing}>
                      {processing ? 'Processing...' : 'Confirm Verification'}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginBottom: 14 }}>Rejecting will notify the student. This cannot be undone.</div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 8 }}>
                        Rejection Reason <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={e => setRejectionReason(e.target.value)}
                        placeholder="Explain why this complaint is being rejected..."
                        rows={4}
                        style={{
                          width: '100%', padding: '10px 14px', borderRadius: 9, fontSize: 13,
                          background: 'rgba(255,255,255,0.07)', border: `1px solid ${rejectionReason.trim() ? 'rgba(255,255,255,0.25)' : 'rgba(239,68,68,0.5)'}`,
                          color: '#fff', outline: 'none', resize: 'vertical', lineHeight: 1.6,
                          boxSizing: 'border-box',
                        }}
                      />
                      {!rejectionReason.trim() && (
                        <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 4 }}>A reason is required before rejecting.</div>
                      )}
                    </div>
                    <button className="ur-btn-hover" style={{ ...s.btnDanger, width: '100%', justifyContent: 'center', display: 'flex', padding: '12px', opacity: !rejectionReason.trim() ? 0.5 : 1 }}
                      onClick={handleReject} disabled={processing || !rejectionReason.trim()}>
                      {processing ? 'Processing...' : 'Confirm Rejection'}
                    </button>
                  </div>
                )}
              </SectionCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VerifyComplaint() {
  return (
    <ThemeProvider>
      <VerifyComplaintInner />
    </ThemeProvider>
  );
}
